import type { IndicatorResponse } from '@/lib/schema/types';

/**
 * Mirrors the FCCERS-3 Scoresheet front page (manual p. 83), minus the three
 * code fields (Observer Code / Facility Code / Provider Code) that DECE does
 * not use. "Disability" is called "special need" throughout, per DECE.
 */
export interface ObservationMetadata {
  observerName: string;
  observerEmail: string;
  /** Network/affiliation the home belongs to (dropdown). */
  networkName: string;
  /**
   * Scoresheet "FCC Home" — the program/home name.
   * Still called providerName on the wire so existing Sheet rows and the
   * dashboard keep resolving; the UI labels it FCC Home.
   */
  providerName: string;
  /** The person running the home — labelled "Provider" in the UI. */
  providerPerson: string;
  /** OCFS licence number of the selected home — the unambiguous key. */
  providerLicenseId: string;
  /** @deprecated Use providerName instead. Kept for old records. */
  siteName: string;
  siteAddress: string;
  observationDate: string; // ISO yyyy-mm-dd
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  /** Adults providing care during the observation. */
  providersPresent: number;
  /** Children enrolled in the child care home. */
  childrenEnrolled: number;
  /** Enrolled children broken down by the scoresheet's age bands. */
  enrollmentByAge: EnrollmentByAge;
  /** Highest number of children the provider is allowed at one time. */
  highestAllowedAtOneTime: number;
  /** Highest number of children present during the observation. */
  childrenPresent: number;
  /** Total children enrolled at any point in the past month. */
  totalEnrolledPastMonth: number;
  /** Children present with an identified special need. */
  childrenSpecialNeeds: number;
  /** Which kinds of special need were identified. */
  specialNeedTypes: string[];
  /** Free text for the "other" special-need option. */
  specialNeedOther: string;
  grossMotorIndoors: string;
  grossMotorOutdoors: string;
  foodPreferences: string;
  foodAllergies: string;
  /** @deprecated Superseded by enrollmentByAge. Kept for old records. */
  ageGroupsPresent: string[];
  /** @deprecated Superseded by providersPresent. Kept for old records. */
  adultsPresent: number;
  notes: string;
}

/** The scoresheet's eight age bands, in the order a child grows. */
export interface EnrollmentByAge {
  m0_5: number;
  m6_11: number;
  m12_17: number;
  m18_23: number;
  m24_35: number;
  y3_5: number;
  y6_8: number;
  y9_12: number;
}

export const AGE_BANDS: Array<{ key: keyof EnrollmentByAge; label: string }> = [
  { key: 'm0_5', label: '0 through 5 months' },
  { key: 'm6_11', label: '6 through 11 months' },
  { key: 'm12_17', label: '12 through 17 months' },
  { key: 'm18_23', label: '18 through 23 months' },
  { key: 'm24_35', label: '24 through 35 months' },
  { key: 'y3_5', label: '3 through 5 years' },
  { key: 'y6_8', label: '6 through 8 years' },
  { key: 'y9_12', label: '9 through 12 years' },
];

export const SPECIAL_NEED_TYPES: Array<{ value: string; label: string }> = [
  { value: 'physical_sensory', label: 'Physical / sensory' },
  { value: 'cognitive_language', label: 'Cognitive / language' },
  { value: 'social_emotional', label: 'Social / emotional' },
  { value: 'other', label: 'Other' },
];

export const emptyEnrollmentByAge = (): EnrollmentByAge => ({
  m0_5: 0, m6_11: 0, m12_17: 0, m18_23: 0,
  m24_35: 0, y3_5: 0, y6_8: 0, y9_12: 0,
});

export type ItemStatus = 'untouched' | 'in_progress' | 'complete';

export interface ObservationRecord {
  id: string;
  instrumentId: 'FCCERS-3';
  schemaVersion: '1.0.0';
  status: 'draft' | 'submitted';
  metadata: ObservationMetadata;
  responses: Record<string, IndicatorResponse>;
  itemNa: Record<string, boolean>;
  itemNotes: Record<string, string>;
  itemStatus: Record<string, ItemStatus>;
  createdAt: number;
  updatedAt: number;
  submittedAt?: number;
  syncedAt?: number;
}

export const emptyMetadata = (): ObservationMetadata => ({
  observerName: '',
  observerEmail: '',
  networkName: '',
  providerName: '',
  providerPerson: '',
  providerLicenseId: '',
  siteName: '',
  siteAddress: '',
  observationDate: new Date().toISOString().slice(0, 10),
  startTime: '',
  endTime: '',
  providersPresent: 0,
  childrenEnrolled: 0,
  enrollmentByAge: emptyEnrollmentByAge(),
  highestAllowedAtOneTime: 0,
  childrenPresent: 0,
  totalEnrolledPastMonth: 0,
  childrenSpecialNeeds: 0,
  specialNeedTypes: [],
  specialNeedOther: '',
  grossMotorIndoors: '',
  grossMotorOutdoors: '',
  foodPreferences: '',
  foodAllergies: '',
  ageGroupsPresent: [],
  adultsPresent: 0,
  notes: '',
});
